import { useMemo, useState } from "react";
import { trpc, type RouterOutputs } from "@/hooks/trpc";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { DeleteConfirmDialog } from "@/components/delete-confirm-dialog";
import { useCan } from "@/hooks/use-operator";
import { Check, Loader2, Pencil, Plus } from "lucide-react";
import type { BrewSensoryFormId, BrewSensoryTemplateId } from "@brewbase/db/types";

type Template = RouterOutputs["brewSensory"]["templates"]["list"][number];
type Form = RouterOutputs["brewSensory"]["forms"]["list"][number];

const TASTE_NONE = "none";

export function BrewSensoryTemplatesSection() {
  const canEdit = useCan("sensory-template:edit");
  // null = list view, "new" = creating, a number = editing that template.
  const [editing, setEditing] = useState<"new" | BrewSensoryTemplateId | null>(
    null,
  );

  const { data: templates = [], isLoading } =
    trpc.brewSensory.templates.list.useQuery();
  const { data: forms = [] } = trpc.brewSensory.forms.list.useQuery();

  const formName = useMemo(() => {
    const map = new Map<BrewSensoryFormId, string>();
    for (const f of forms) map.set(f.id, f.name);
    return map;
  }, [forms]);

  return (
    <div className="space-y-4 max-w-3xl">
      <p className="text-xs text-muted-foreground">
        Pick a template when creating a session to pre-fill the welcome
        instructions and the aroma/taste forms. Later edits here never affect
        sessions already created.
      </p>

      {editing === "new" ? (
        <TemplateEditor forms={forms} onDone={() => setEditing(null)} />
      ) : (
        <>
          <div className="flex items-center justify-between">
            <p className="text-sm text-muted-foreground">
              {isLoading
                ? "Loading templates…"
                : templates.length === 0
                  ? "No templates yet."
                  : `${templates.length} template${templates.length !== 1 ? "s" : ""}.`}
            </p>
            {canEdit && (
              <Button size="sm" onClick={() => setEditing("new")}>
                <Plus className="h-4 w-4 mr-1.5" />
                Add Template
              </Button>
            )}
          </div>

          {templates.length > 0 && (
            <div className="space-y-2">
              {templates.map((t) =>
                editing === t.id ? (
                  <TemplateEditor
                    key={t.id}
                    template={t}
                    forms={forms}
                    onDone={() => setEditing(null)}
                  />
                ) : (
                  <TemplateRow
                    key={t.id}
                    template={t}
                    formName={formName}
                    canEdit={canEdit}
                    onEdit={() => setEditing(t.id)}
                  />
                ),
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}

function TemplateRow({
  template,
  formName,
  canEdit,
  onEdit,
}: {
  template: Template;
  formName: Map<BrewSensoryFormId, string>;
  canEdit: boolean;
  onEdit: () => void;
}) {
  const utils = trpc.useUtils();
  const archiveMutation = trpc.brewSensory.templates.archive.useMutation({
    onSuccess: () => utils.brewSensory.templates.list.invalidate(),
    onError: (err) => toast.error(err.message),
  });

  return (
    <div className="flex items-start gap-3 border bg-card px-4 py-3">
      <div className="flex-1 min-w-0 space-y-1">
        <div className="flex items-center gap-2 flex-wrap">
          <p className="text-sm font-medium leading-snug">{template.name}</p>
          <span className="inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-medium bg-violet-100 text-violet-700">
            {template.aroma_form_id
              ? (formName.get(template.aroma_form_id) ?? "Aroma")
              : "Aroma"}
          </span>
          {template.taste_form_id && (
            <span className="inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-medium bg-amber-100 text-amber-700">
              {formName.get(template.taste_form_id) ?? "Taste"}
            </span>
          )}
        </div>
        <p className="text-xs text-muted-foreground line-clamp-2 whitespace-pre-wrap">
          {template.instructions?.trim() || "No welcome instructions."}
        </p>
      </div>

      {canEdit && (
        <div className="flex items-center gap-1 shrink-0">
          <Button
            variant="ghost"
            size="icon"
            className="h-7 w-7 text-muted-foreground hover:text-foreground"
            onClick={onEdit}
          >
            <Pencil className="h-3.5 w-3.5" />
          </Button>
          <DeleteConfirmDialog
            title="Delete template?"
            description={`Delete the "${template.name}" template? Sessions already created from it are not affected.`}
            isDeleting={archiveMutation.isPending}
            onConfirm={() => archiveMutation.mutate(template.id)}
          />
        </div>
      )}
    </div>
  );
}

function TemplateEditor({
  template,
  forms,
  onDone,
}: {
  template?: Template;
  forms: Form[];
  onDone: () => void;
}) {
  const utils = trpc.useUtils();
  const aromaForms = forms.filter((f) => f.category === "aroma");
  const tasteForms = forms.filter((f) => f.category === "taste");

  const [name, setName] = useState(template?.name ?? "");
  const [aromaFormId, setAromaFormId] = useState<BrewSensoryFormId | null>(
    template?.aroma_form_id ?? aromaForms[0]?.id ?? null,
  );
  const [tasteFormId, setTasteFormId] = useState<BrewSensoryFormId | null>(
    template?.taste_form_id ?? null,
  );
  const [instructions, setInstructions] = useState(
    template?.instructions ?? "",
  );

  const invalidate = () => utils.brewSensory.templates.list.invalidate();

  const createMutation = trpc.brewSensory.templates.create.useMutation({
    onSuccess: () => {
      invalidate();
      onDone();
    },
    onError: (err) => toast.error(err.message),
  });
  const updateMutation = trpc.brewSensory.templates.update.useMutation({
    onSuccess: () => {
      invalidate();
      onDone();
    },
    onError: (err) => toast.error(err.message),
  });

  const isPending = createMutation.isPending || updateMutation.isPending;

  const handleSave = () => {
    const trimmedName = name.trim();
    if (!trimmedName) {
      toast.error("Template name is required.");
      return;
    }
    if (!aromaFormId) {
      toast.error("An aroma form is required.");
      return;
    }
    const payload = {
      name: trimmedName,
      instructions: instructions.trim() || null,
      aroma_form_id: aromaFormId,
      taste_form_id: tasteFormId,
    };
    if (template) {
      updateMutation.mutate({ id: template.id, ...payload });
    } else {
      createMutation.mutate(payload);
    }
  };

  return (
    <div className="space-y-3 border bg-card px-4 py-3">
      <div className="space-y-1.5">
        <Label htmlFor="template-name">Name</Label>
        <Input
          id="template-name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="e.g. Standard tasting panel"
        />
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-1.5">
          <Label>Aroma form</Label>
          <Select
            value={aromaFormId ? String(aromaFormId) : undefined}
            onValueChange={(v) =>
              setAromaFormId(Number(v) as unknown as BrewSensoryFormId)
            }
          >
            <SelectTrigger className="h-8 text-sm">
              <SelectValue placeholder="Select a form" />
            </SelectTrigger>
            <SelectContent>
              {aromaForms.map((f) => (
                <SelectItem key={f.id} value={String(f.id)}>
                  {f.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-1.5">
          <Label>Taste form</Label>
          <Select
            value={tasteFormId ? String(tasteFormId) : TASTE_NONE}
            onValueChange={(v) =>
              setTasteFormId(
                v === TASTE_NONE
                  ? null
                  : (Number(v) as unknown as BrewSensoryFormId),
              )
            }
          >
            <SelectTrigger className="h-8 text-sm">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={TASTE_NONE}>No taste evaluation</SelectItem>
              {tasteForms.map((f) => (
                <SelectItem key={f.id} value={String(f.id)}>
                  {f.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="space-y-1.5">
        <Label htmlFor="template-instructions">Welcome instructions</Label>
        <Textarea
          id="template-instructions"
          value={instructions}
          onChange={(e) => setInstructions(e.target.value)}
          placeholder="Write the instructions analysts will see on the welcome screen before starting their evaluation…"
          rows={6}
          className="text-sm"
        />
      </div>

      <div className="flex items-center gap-2 justify-end">
        <Button variant="ghost" size="sm" className="h-7 text-xs" onClick={onDone}>
          Cancel
        </Button>
        <Button
          size="sm"
          className="h-7 text-xs"
          disabled={isPending}
          onClick={handleSave}
        >
          {isPending ? (
            <Loader2 className="h-3 w-3 mr-1 animate-spin" />
          ) : (
            <Check className="h-3 w-3 mr-1" />
          )}
          Save
        </Button>
      </div>
    </div>
  );
}
